import {
    formatPhoneNumber,
    MakeCallsFrom,
    PhoneNumberE164,
    TelecomPhoneLineId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { useCommitUserPreference } from "components/manage/account-settings/account-settings-hooks";
import { trpc } from "components/rpc/trpc";
import { InternalLink } from "components/ui/internal-link";
import { Radio } from "components/ui/radio";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useId, useState } from "react";
import { usePreferences } from "store/selectors/hooks";

export const phoneSettingsPath = "/settings/account/phone";

export const callUsingPhoneHint =
    "We'll call your phone first, then connect you to the contact.";

export type MyPhone =
    | { kind: "callable"; phoneNumber: PhoneNumberE164 }
    | { kind: "missing" }
    | { kind: "unusable" };

export type BridgeCalling = inferRouterOutputs<AppRouter>["telecom"]["getBridgeCalling"];

const myPhoneOf = (bridgeCalling: BridgeCalling): MyPhone => {
    if (bridgeCalling.ringPhoneNumber) {
        return { kind: "callable", phoneNumber: bridgeCalling.ringPhoneNumber };
    }
    return bridgeCalling.bridgeCallPhoneNumber || bridgeCalling.personalPhoneNumber
        ? { kind: "unusable" }
        : { kind: "missing" };
};

export const effectiveCallFrom = (
    preference: MakeCallsFrom,
    myPhone: MyPhone,
): MakeCallsFrom => (myPhone.kind === "callable" ? preference : "browser");

const myPhoneLabel = (myPhone: MyPhone): string =>
    myPhone.kind === "callable"
        ? `Your phone, ${formatPhoneNumber(myPhone.phoneNumber)}`
        : "Your phone";

const phoneSettingsLinkText = (myPhone: MyPhone): string | null => {
    switch (myPhone.kind) {
        case "callable":
            return null;
        case "missing":
            return "Add your phone number in SMS & Voice Settings";
        case "unusable":
            return "Update your phone number in SMS & Voice Settings";
    }
    const exhaustiveCheck: never = myPhone;
    throw new Error(`Unhandled phone: ${JSON.stringify(exhaustiveCheck)}`);
};

interface CallFromOptionsProps {
    label: string;
    callFrom: MakeCallsFrom;
    myPhone: MyPhone;
    onChange: (callFrom: MakeCallsFrom) => void;
}

export const CallFromOptions: React.FC<CallFromOptionsProps> = ({
    label,
    callFrom,
    myPhone,
    onChange,
}) => {
    const radioGroupId = useId();
    const chooseComputer = useCallback(() => onChange("browser"), [onChange]);
    const choosePhone = useCallback(() => onChange("phone"), [onChange]);
    const linkText = phoneSettingsLinkText(myPhone);

    return (
        <div
            role="radiogroup"
            aria-labelledby={`${radioGroupId}-label`}
            className="space-y-2">
            <div id={`${radioGroupId}-label`}>{label}</div>
            <Radio
                name={radioGroupId}
                checked={callFrom === "browser"}
                onChange={chooseComputer}
                label="Your computer"
            />
            <Radio
                name={radioGroupId}
                checked={callFrom === "phone"}
                disabled={myPhone.kind !== "callable"}
                onChange={choosePhone}
                label={myPhoneLabel(myPhone)}
            />
            {linkText ? (
                <div className="ml-6">
                    <InternalLink to={phoneSettingsPath}>{linkText}</InternalLink>
                </div>
            ) : null}
            {callFrom === "phone" ? (
                <p className="ml-6 text-sm text-gray-500">{callUsingPhoneHint}</p>
            ) : null}
        </div>
    );
};

export type CallFromChoice =
    | { status: "loading"; callFrom: MakeCallsFrom }
    | { status: "error"; callFrom: MakeCallsFrom; retrying: boolean; retry: () => void }
    | {
          status: "ready";
          callFrom: MakeCallsFrom;
          preference: MakeCallsFrom;
          myPhone: MyPhone;
          bridgeCalling: BridgeCalling;
          chooseCallFrom: (callFrom: MakeCallsFrom) => void;
      };

export const useCallFrom = (phoneLineId: TelecomPhoneLineId): CallFromChoice => {
    const preferences = usePreferences();
    const commitCallFrom = useCommitUserPreference("makeCallsFrom");
    const [chosenCallFrom, setChosenCallFrom] = useState<MakeCallsFrom | null>(null);
    const trpcClient = trpc.useTRPC();
    const bridgeCalling = useQuery({
        ...trpcClient.telecom.getBridgeCalling.queryOptions({ phoneLineId }),
    });

    const chooseCallFrom = useCallback(
        (callFrom: MakeCallsFrom) => {
            setChosenCallFrom(callFrom);
            commitCallFrom(callFrom);
        },
        [commitCallFrom],
    );
    const retry = useCallback(() => {
        bridgeCalling.refetch().catch((): void => undefined);
    }, [bridgeCalling]);

    const preference = chosenCallFrom ?? preferences.makeCallsFrom;
    if (bridgeCalling.data) {
        const myPhone = myPhoneOf(bridgeCalling.data);
        return {
            status: "ready",
            callFrom: effectiveCallFrom(preference, myPhone),
            preference,
            myPhone,
            bridgeCalling: bridgeCalling.data,
            chooseCallFrom,
        };
    }
    if (bridgeCalling.isError) {
        return {
            status: "error",
            callFrom: preference,
            retrying: bridgeCalling.isFetching,
            retry,
        };
    }
    return { status: "loading", callFrom: preference };
};

interface CallFromChoiceOptionsProps {
    label: string;
    choice: CallFromChoice;
}

export const CallFromChoiceOptions: React.FC<CallFromChoiceOptionsProps> = ({
    label,
    choice,
}) => {
    switch (choice.status) {
        case "loading":
            return (
                <div className="space-y-2">
                    <div>{label}</div>
                    <Spinner />
                </div>
            );
        case "error":
            return (
                <RetryableErrorBanner
                    message="We couldn't load your phone number."
                    retrying={choice.retrying}
                    onRetry={choice.retry}
                />
            );
        case "ready":
            return (
                <CallFromOptions
                    label={label}
                    callFrom={choice.callFrom}
                    myPhone={choice.myPhone}
                    onChange={choice.chooseCallFrom}
                />
            );
    }
    const exhaustiveCheck: never = choice;
    throw new Error(`Unhandled call-from status: ${JSON.stringify(exhaustiveCheck)}`);
};
